import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Flashlight, FlashlightOff, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

let _seq = 0;

const BarcodeScanner = ({ onScan, onClose, manualPlaceholder = 'Skriv inn serienummer' }) => {
  const containerId = useRef(`bc-scanner-${++_seq}`).current;
  const scannerRef = useRef(null);
  const trackRef = useRef(null);
  const handledRef = useRef(false);
  const [manualInput, setManualInput] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [starting, setStarting] = useState(true);

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    trackRef.current = null;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch (err) { console.warn('[BarcodeScanner]', err); }
      try { s.clear(); } catch (err) { console.warn('[BarcodeScanner]', err); }
    }
  };

  const finish = (value) => {
    if (handledRef.current) return;
    handledRef.current = true;
    stop().then(() => onScan(value));
  };

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      const scanner = new Html5Qrcode(containerId, {
        formatsToSupport: FORMATS,
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      });
      scannerRef.current = scanner;
      const scanConfig = {
        fps: 10,
        qrbox: (vw, vh) => {
          const w = Math.floor(Math.min(vw, vh) * 0.8);
          return { width: w, height: Math.max(80, Math.floor(w * 0.55)) };
        },
        aspectRatio: 1.777,
      };
      const onDecode = (decodedText) => finish((decodedText || '').trim());
      const onErr = () => { /* ignore per-frame decode errors */ };

      // Try rear camera first, then fall back to any available camera.
      const attempts = [
        { facingMode: { exact: 'environment' } },
        { facingMode: 'environment' },
      ];
      let started = false;
      for (const camConfig of attempts) {
        try {
          await scanner.start(camConfig, scanConfig, onDecode, onErr);
          started = true;
          break;
        } catch (e) {
          if (cancelled) return;
        }
      }
      if (!started) {
        // Last resort: pick the first enumerated camera
        try {
          const devices = await Html5Qrcode.getCameras();
          if (devices && devices.length) {
            await scanner.start(devices[devices.length - 1].id, scanConfig, onDecode, onErr);
            started = true;
          }
        } catch (err) { console.warn('[BarcodeScanner] camera fallback', err); }
      }

      if (cancelled) { await stop(); return; }
      if (!started) {
        setStarting(false);
        toast.error('Kunne ikke starte kamera. Skriv inn serienummer manuelt.');
        return;
      }
      setStarting(false);
      // Detect torch capability from the live video track
      try {
        const video = document.getElementById(containerId)?.querySelector('video');
        const track = video?.srcObject?.getVideoTracks?.()[0];
        if (track) {
          trackRef.current = track;
          const caps = track.getCapabilities?.();
          if (caps && 'torch' in caps && caps.torch) setTorchSupported(true);
        }
      } catch (err) { console.warn('[BarcodeScanner]', err); }
    };
    start();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch {
      toast.error('Lommelykt støttes ikke på denne enheten');
    }
  };

  const submitManual = () => {
    const v = manualInput.trim();
    if (v) finish(v);
  };

  return (
    <div className="space-y-3" data-testid="barcode-scanner">
      <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: 240 }}>
        <div id={containerId} className="w-full" />
        {starting && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
            Starter kamera…
          </div>
        )}
        {torchSupported && (
          <button
            type="button"
            onClick={toggleTorch}
            data-testid="barcode-torch-toggle"
            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
          >
            {torchOn ? <FlashlightOff className="h-5 w-5" /> : <Flashlight className="h-5 w-5" />}
          </button>
        )}
      </div>
      <p className="text-xs text-center text-slate-500">Hold strekkoden eller QR-koden innenfor rammen</p>
      <div className="flex gap-2">
        <Input
          value={manualInput}
          onChange={(e) => setManualInput(e.target.value)}
          placeholder={manualPlaceholder}
          data-testid="barcode-manual-input"
          onKeyDown={(e) => { if (e.key === 'Enter') submitManual(); }}
        />
        <Button type="button" onClick={submitManual} disabled={!manualInput.trim()} data-testid="barcode-confirm">
          <Check className="h-4 w-4" />
        </Button>
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={() => { stop().then(onClose); }}
        className="w-full text-slate-500"
        data-testid="barcode-cancel"
      >
        <X className="h-4 w-4 mr-1" /> Avbryt
      </Button>
    </div>
  );
};

export default BarcodeScanner;
